// utils/formatters.js
// Formatting utilities for chart data display

/**
 * Format price values with appropriate decimal places
 */
export const formatPrice = (value) => {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  // For prices under $1, show 4 decimal places
  if (num < 1) return num.toFixed(4);
  // For prices under $100, show 2 decimal places
  if (num < 100) return num.toFixed(2);
  // For larger prices, show 2 decimal places with commas
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format volume with abbreviations (K, M, B)
 */
export const formatVolume = (value) => {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  if (num >= 1e9) {
    return (num / 1e9).toFixed(2) + 'B';
  }
  if (num >= 1e6) {
    return (num / 1e6).toFixed(2) + 'M';
  }
  if (num >= 1e3) {
    return (num / 1e3).toFixed(2) + 'K';
  }
  return num.toFixed(0);
};

/**
 * Format percentage change
 */
export const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';

  const num = parseFloat(value);
  if (isNaN(num)) return 'N/A';

  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

/**
 * Format date for display
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return 'N/A';

  const d = new Date(date);
  if (isNaN(d.getTime())) return 'N/A';

  switch (format) {
    case 'full':
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'medium':
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    case 'short':
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    case 'time':
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    case 'datetime':
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    default:
      return d.toLocaleDateString('en-US');
  }
};

/**
 * Format large numbers with abbreviations
 */
export const formatLargeNumber = (number) => {
  if (number == null || isNaN(number)) {
    return 'N/A';
  }

  // Handle negative numbers by using absolute value, then add sign back
  const isNegative = number < 0;
  const absNumber = Math.abs(number);

  const billion = 1000000000;
  const million = 1000000;
  const thousand = 1000;

  let formatted;

  if (absNumber >= billion) {
    formatted = (absNumber / billion).toFixed(2) + 'B';
  } else if (absNumber >= million) {
    formatted = (absNumber / million).toFixed(2) + 'M';
  } else if (absNumber >= thousand) {
    formatted = (absNumber / thousand).toFixed(2) + 'K';
  } else {
    formatted = absNumber.toFixed(2);
  }

  return isNegative ? '-' + formatted : formatted;
};

/**
 * Format tooltip content for chart
 */
export const formatTooltip = (params, indicators = {}) => {
  if (!params || !params[0]) return '';

  const data = params[0].data;
  const date = formatDate(data[0], 'medium');

  let content = `<div style="padding: 10px;">
    <div style="font-weight: bold; margin-bottom: 8px;">${date}</div>`;

  // OHLC data
  if (params[0].seriesType === 'candlestick') {
    const [, open, close, low, high] = data;
    const change = close - open;
    const changePercent = (change / open) * 100;
    const color = change >= 0 ? '#0eb35b' : '#e62333';

    content += `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 12px;">
        <div>Open: ${formatPrice(open)}</div>
        <div>High: ${formatPrice(high)}</div>
        <div>Close: ${formatPrice(close)}</div>
        <div>Low: ${formatPrice(low)}</div>
        <div style="grid-column: span 2; color: ${color};">
          Change: ${formatPrice(change)} (${formatPercent(changePercent)})
        </div>
      </div>`;
  }

  // Volume
  const volumeParam = params.find(p => p.seriesName === 'Volume');
  if (volumeParam) {
    content += `
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
        <div style="font-size: 12px;">Volume: ${formatVolume(volumeParam.value[1])}</div>
      </div>`;
  }

  // Indicators
  const indicatorParams = params.filter(p =>
    p.seriesType === 'line' && p.seriesName !== 'Volume'
  );

  if (indicatorParams.length > 0) {
    content += `
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">`;

    indicatorParams.forEach(param => {
      if (param.value[1] !== null) {
        content += `
          <div style="font-size: 12px; display: flex; align-items: center;">
            <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; margin-right: 6px; border-radius: 2px;"></span>
            ${param.seriesName}: ${formatPrice(param.value[1])}
          </div>`;
      }
    });

    content += `</div>`;
  }

  content += `</div>`;

  return content;
};

/**
 * Format axis label based on value range
 */
export const formatAxisLabel = (value, type = 'price') => {
  if (type === 'volume') {
    return formatVolume(value);
  }
  if (type === 'percent') {
    return formatPercent(value);
  }
  return formatPrice(value);
};

/**
 * Get color for price change
 */
export const getPriceChangeColor = (change) => {
  if (change > 0) return '#0eb35b';
  if (change < 0) return '#e62333';
  return 'rgba(255, 255, 255, 0.7)';
};